<script setup lang="ts">
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { setTitle } from '~/utils/dom'
import { colors, getColorForId } from '~/utils/colors'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import useSearchHelper from '~/composables/useSearchHelper'
import { useContentHelper } from '~/composables/useContentHelper'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import ProductPackageIntro from '~/components/ResourceFinder/ProductPackageIntro.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import StackedCard from '~/components/cards/StackedCard.vue'
import SearchCard from '~/components/cards/SearchCard.vue'
import BookCard from '~/components/cards/BookCard.vue'

const props = defineProps<{ locationId: number }>()

const { findContents } = useContentApi()
const { packageTypes } = useContentHelper()
const { cardFields } = useSearchHelper()
const { isPackage, isProduct, isBookReader, isTeacherArticle } = useContentHelper()
const { checkItemLicense } = useLicenseControlStore()
const { isAuthenticated } = storeToRefs(useAuthStore())

const { data, isLoading, isError } = useQuery({
  enabled: computed(() => !!props.locationId),
  queryKey: computed(() => ['package', props.locationId]),
  queryFn: async () => findContents<ContentPackage>({
    locationIdCriterion: [props.locationId],
    contentTypeCriterion: packageTypes,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
  retry: 1,
})

const { data: children, isLoading: isLoadingChildren } = useQuery({
  enabled: computed(() => !!data?.value && isAuthenticated.value),
  queryKey: computed(() => ['package-children', props.locationId]),
  queryFn: () => findContents<BaseItem>({
    parentLocationIdCriterion: [data.value!.mainLocationId],
    sortField: data.value!.sortField,
    sortOrder: data.value!.sortOrder,
  }, 24, 0, cardFields),
  select: children => children.filter(child => !isTeacherArticle(child)),
})

const isPackageCard = (resource: BaseItem) => isPackage(resource) || isProduct(resource)
const isBookCard = (resource: BaseItem) => isBookReader(resource)

watch(data, () => {
  if (!data.value)
    return
  setTitle(data.value.title)
  checkItemLicense(data.value)
}, { immediate: true })
</script>

<template>
  <NotFound v-if="isError" />
  <AuSkeletonWrapper
    v-else-if="isLoading || isLoadingChildren"
    class="mb-12"
  >
    <div class="bg-gray-5/50 mb-8 w-full py-8">
      <div class="max-w-screen-au mx-auto space-x-4 px-4 pb-8 sm:px-8">
        <AuSkeleton
          v-for="i in 3"
          :key="i"
          width="120px"
          height="30px"
        />
      </div>
      <div class="max-w-screen-au mx-auto grid grid-cols-1 items-center gap-8 px-4 sm:px-8 md:grid-cols-2">
        <AuSkeleton
          height="400px"
          width="100%"
        />
        <div>
          <AuSkeleton
            height="40px"
            width="100%"
            class="mb-4"
          />
          <AuSkeleton
            v-for="i in 4"
            :key="i"
            height="20px"
            width="100%"
          />
        </div>
      </div>
    </div>
    <div class="max-w-screen-au xs:grid-cols-2 mx-auto grid auto-rows-fr grid-cols-1 gap-4 px-4 sm:px-8 md:grid-cols-3">
      <AuSkeleton
        v-for="i in 16"
        :key="i"
        height="160px"
      />
    </div>
  </AuSkeletonWrapper>
  <div
    v-else
    class="mb-12"
  >
    <TopBar
      :item="data"
      :favorite="false"
      :fullscreen="false"
    />
    <ProductPackageIntro
      v-if="data"
      :content="data"
    />
    <ul
      v-if="children && children.length > 0"
      class="max-w-screen-au xs:grid-cols-2 mx-auto grid auto-rows-fr grid-cols-1 gap-4 px-4 sm:px-8 md:grid-cols-3"
    >
      <li
        v-for="item in children"
        :key="`resource-${item.locationId}`"
        class="animate-fade row-span-1"
        :class="{
          'row-span-2': isPackageCard(item) && item.thumbnail,
          'row-span-4': isBookCard(item),
        }"
      >
        <StackedCard
          v-if="isPackageCard(item)"
          :item
          class="size-full"
          element="div"
          padded-stack
          :color="getColorForId(item.locationId, colors)"
        >
          <template #default="{ item: searchItem }">
            <SearchCard
              v-if="searchItem"
              :item="searchItem"
              :show-path="false"
              package-style
            />
          </template>
        </StackedCard>
        <BookCard
          v-else-if="isBookCard(item)"
          :item
          class="hover:bg-purple-10 !flex flex-col"
        />
        <SearchCard
          v-else
          :item
          :show-path="false"
        />
      </li>
    </ul>
  </div>
</template>
